<template>
    <div class="pa-ui pa-page">
        <header class="pa-page-header">
            <div class="pa-page-header__row">
                <div>
                    <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                        <h1 class="pa-title">Adesão ao Plano</h1>
                        <pa-badge v-if="campaign" :status="campaign.status" />
                    </div>
                    <p class="pa-subtitle">
                        <template v-if="campaign">{{ windowText }}</template>
                        <template v-else>Termo de adesão, plano de recuperação judicial e acompanhamento das adesões.</template>
                    </p>
                </div>
            </div>
            <div v-if="campaign" class="pa-steps mt-3">
                <span v-for="step in checklist" :key="step.label" class="pa-step" :class="{ 'pa-step--done': step.done }">
                    <v-icon :color="step.done ? 'success' : '#a1a1aa'">{{ step.done ? "mdi-check-circle" : "mdi-circle-outline" }}</v-icon>
                    {{ step.label }}
                </span>
            </div>
        </header>

        <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

        <div v-else-if="notOpened" class="pa-card">
            <div class="pa-empty">
                <v-icon large color="#a1a1aa">mdi-file-sign</v-icon>
                <div>{{ notOpened }}</div>
            </div>
        </div>

        <template v-else-if="campaign">
            <v-tabs v-model="tab" class="pa-tabs" show-arrows>
                <v-tab>Termo e opções de pagamento</v-tab>
                <v-tab>Plano de recuperação judicial</v-tab>
                <v-tab>Analítico</v-tab>
            </v-tabs>
            <v-tabs-items v-model="tab" class="pt-5">
                <v-tab-item>
                    <plan-adhesion-templates
                        mode="client"
                        :templates="campaign.templates"
                        :current-template-id="campaign.current_template_id"
                        :catalog="catalog"
                        :class-names="campaign.class_names"
                        :voting-class-types="campaign.voting_class_types"
                        :urls="templateUrls"
                        @changed="load" />
                </v-tab-item>
                <v-tab-item>
                    <plan-adhesion-plan-documents
                        :documents="campaign.plan_documents"
                        upload-url="/api/client/plan-adhesion/plan-documents"
                        :file-url="(doc) => `/api/client/plan-adhesion/plan-documents/${doc.id}/file`"
                        upload-hint="O plano enviado passa pela aprovação do Administrador Judicial antes de ficar disponível aos credores."
                        @changed="load" />
                </v-tab-item>
                <v-tab-item>
                    <plan-adhesion-analytics :urls="analyticsUrls" />
                </v-tab-item>
            </v-tabs-items>
        </template>
    </div>
</template>

<script>
import axios from "axios";
import { eventHub } from "@/main";
import PlanAdhesionPlanDocuments from "@/components/plan-adhesion/PlanAdhesionPlanDocuments.vue";
import PlanAdhesionTemplates from "@/components/plan-adhesion/PlanAdhesionTemplates.vue";
import PlanAdhesionAnalytics from "@/components/plan-adhesion/PlanAdhesionAnalytics.vue";
import PaBadge from "@/components/plan-adhesion/PaBadge.vue";
import { errorMessage, formatDateTime } from "@/components/plan-adhesion/plan-adhesion.util";
import "@/components/plan-adhesion/pa-ui.scss";

// Adesao ao Plano no painel da recuperanda: propor o termo (com opcoes de
// pagamento) e enviar o plano; tudo passa pela aprovacao do AJ.
export default {
    name: "PlanAdhesion",

    components: { PaBadge, PlanAdhesionPlanDocuments, PlanAdhesionTemplates, PlanAdhesionAnalytics },

    data: () => ({
        campaign: null,
        catalog: [],
        loading: true,
        notOpened: null,
        tab: 0,
        analyticsUrls: {
            analytics: "/api/client/plan-adhesion/analytics",
            pdf: "/api/client/plan-adhesion/report-pdf",
            xlsx: "/api/client/plan-adhesion/report-xlsx",
        },
        templateUrls: {
            create: "/api/client/plan-adhesion/templates",
            template: (id) => `/api/client/plan-adhesion/templates/${id}`,
            action: (id, action) => `/api/client/plan-adhesion/templates/${id}/${action}`,
        },
    }),

    computed: {
        windowText() {
            const c = this.campaign;
            if (!c.opens_at) return "Janela de adesão ainda não configurada pelo Administrador Judicial.";
            return `Adesão de ${formatDateTime(c.opens_at)} ${c.closes_at ? `até ${formatDateTime(c.closes_at)}` : "sem data de encerramento"}`;
        },

        checklist() {
            const c = this.campaign;
            return [
                { label: "Plano publicado", done: !!c.current_plan_document_id },
                { label: "Termo publicado", done: !!c.current_template_id },
                { label: "Adesão aberta aos credores", done: c.status === "open" },
            ];
        },
    },

    created() {
        this.load();
        this.loadCatalog();
        eventHub.$on("client-context-changed", this.load);
    },

    beforeDestroy() {
        eventHub.$off("client-context-changed", this.load);
    },

    methods: {
        formatDateTime,

        async load() {
            try {
                const result = await axios.get("/api/client/plan-adhesion");
                this.campaign = result.data.content;
                this.notOpened = null;
            } catch (error) {
                this.campaign = null;
                if (error.response && error.response.status === 404) this.notOpened = errorMessage(error);
                else this.$dialog.notify.error(errorMessage(error), { position: "top-right", timeout: 5000 });
            } finally {
                this.loading = false;
            }
        },

        async loadCatalog() {
            try {
                const result = await axios.get("/api/client/plan-adhesion/placeholders");
                this.catalog = result.data.content;
            } catch (error) {
                this.catalog = [];
            }
        },
    },
};
</script>
